import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { apiFetch } from '../api/client';
import { DisclaimerModal } from '../components/ui/DisclaimerModal';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore(s => s.setAuth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [appVersion, setAppVersion] = useState<string | null>(null);
  const [googleEnabled, setGoogleEnabled] = useState<boolean | null>(null);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [disclaimerSlug, setDisclaimerSlug] = useState<'privacy_policy' | 'cookie_policy' | 'note_legali'>('privacy_policy');
  const resetSuccess = (location.state as { resetSuccess?: boolean } | null)?.resetSuccess;

  useEffect(() => {
    apiFetch<{ version: string }>('/version')
      .then(r => setAppVersion(r.version))
      .catch(() => setAppVersion(null));
    apiFetch<{ enabled: boolean }>('/system/google-enabled')
      .then(r => setGoogleEnabled(r.enabled))
      .catch(() => setGoogleEnabled(false));
  }, []);

  const searchParams = new URLSearchParams(location.search);
  const googleError = searchParams.get('error');
  const googleErrorMsg = googleError === 'google_cancelled'
    ? 'Accesso con Google annullato.'
    : googleError === 'google_auth_failed'
    ? 'Errore durante l\'accesso con Google. Riprova.'
    : googleError === 'google_not_whitelisted'
    ? 'Accesso Google negato. La tua email non è autorizzata. Contatta l\'amministratore.'
    : googleError === 'google_whitelist_empty'
    ? 'Accesso Google temporaneamente disabilitato. Usa email e password per accedere.'
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await apiFetch<{ accessToken: string; user?: { id: string; email: string; name: string; role: string }; twoFactorPending?: boolean }>(
        '/auth/login',
        { method: 'POST', body: JSON.stringify({ email, password }) }
      );
      if (data.twoFactorPending) {
        setAuth(data.accessToken, null as unknown as Parameters<typeof setAuth>[1]);
        useAuthStore.getState().setTwoFactorPending(true, data.accessToken);
        navigate('/auth/2fa-verify');
        return;
      }
      if (data.user) {
        setAuth(data.accessToken, data.user);
      }
      navigate('/dashboard');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--color-bg)',
      padding: '1rem',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Blob 1 */}
      <div style={{
        position: 'absolute',
        width: '600px', height: '600px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, oklch(0.45 0.07 195 / 0.15), transparent 70%)',
        top: '-200px', left: '-150px',
        animation: 'blob1 20s ease-in-out infinite alternate',
        pointerEvents: 'none',
      }} />
      {/* Blob 2 */}
      <div style={{
        position: 'absolute',
        width: '500px', height: '500px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, oklch(0.55 0.05 195 / 0.10), transparent 70%)',
        bottom: '-150px', right: '-100px',
        animation: 'blob2 20s ease-in-out infinite alternate',
        pointerEvents: 'none',
      }} />

      <div style={{
        width: '100%',
        maxWidth: 'min(400px, 100vw - 2rem)',
        display: 'flex',
        flexDirection: 'column',
        gap: '2rem',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* Logo + titolo */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem',
          animation: 'fadeSlideUp 0.6s var(--ease-spring) both',
        }}>
          <div style={{
            width: 52, height: 52,
            borderRadius: 14,
            background: 'var(--color-primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 28px oklch(0.45 0.07 195 / 0.25)',
          }}>
            <span style={{
              fontFamily: 'Satoshi, system-ui, sans-serif', fontWeight: 700,
              fontSize: 34,
              color: '#fff',
              lineHeight: 1,
            }}>LC</span>
          </div>
          <div style={{ textAlign: 'center' }}>
            <h1 style={{
              fontFamily: 'Satoshi, system-ui, sans-serif', fontWeight: 700,
              fontSize: '1.75rem',
              color: 'var(--color-text)',
              marginBottom: '0.25rem',
            }}>LensCRM</h1>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              Gestione eventi fotografici
            </p>
            {appVersion && (
              <p style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--color-text-faint)',
                fontStyle: 'italic',
                marginTop: '0.5rem',
              }}>
                v{appVersion}
              </p>
            )}
          </div>
        </div>

        {/* Card glass */}
        <div style={{
          background: 'var(--color-glass-bg)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid var(--color-glass-border)',
          borderRadius: 'var(--radius-container)',
          padding: '2rem',
          boxShadow: '0 24px 64px oklch(0 0 0 / 0.45)',
          animation: 'fadeSlideUp 0.6s var(--ease-spring) 150ms both',
        }}>
          {resetSuccess && (
            <div style={{
              background: 'var(--color-success-highlight)',
              border: '1px solid var(--color-success)',
              borderRadius: 'var(--radius-card)',
              padding: '0.625rem 0.875rem',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-success)',
              marginBottom: '0.5rem',
            }}>
              Password reimpostata con successo. Accedi con la nuova password.
            </div>
          )}
          {googleErrorMsg && (
            <div style={{
              background: 'var(--color-error-highlight)',
              border: '1px solid var(--color-error)',
              borderRadius: 'var(--radius-card)',
              padding: '0.625rem 0.875rem',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-error)',
              marginBottom: '0.5rem',
            }}>
              {googleErrorMsg}
            </div>
          )}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              <label htmlFor="email" style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-muted)' }}>
                Email
              </label>
              <input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
                placeholder="admin@example.com"
                style={{
                  background: 'var(--color-glass-input-bg)',
                  border: `1px solid ${error ? 'var(--color-error)' : 'var(--color-glass-border)'}`,
                  borderRadius: 'var(--radius-card)',
                  color: 'var(--color-text)',
                  fontSize: 'var(--text-sm)',
                  padding: '0.625rem 0.875rem',
                  width: '100%',
                  outline: 'none',
                  transition: 'border-color var(--transition-interactive), box-shadow var(--transition-interactive)',
                }}
                onFocus={e => {
                  e.target.style.borderColor = 'var(--color-primary)';
                  e.target.style.boxShadow = '0 0 0 3px oklch(0.45 0.07 195 / 0.15)';
                }}
                onBlur={e => {
                  e.target.style.borderColor = error ? 'var(--color-error)' : 'var(--color-glass-border)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
              <label htmlFor="password" style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-muted)' }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="password"
                  data-testid="login-password"
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  style={{
                    background: 'var(--color-glass-input-bg)',
                    border: `1px solid ${error ? 'var(--color-error)' : 'var(--color-glass-border)'}`,
                    borderRadius: 'var(--radius-card)',
                    color: 'var(--color-text)',
                    fontSize: 'var(--text-sm)',
                    padding: '0.625rem 2.75rem 0.625rem 0.875rem',
                    width: '100%',
                    outline: 'none',
                    transition: 'border-color var(--transition-interactive), box-shadow var(--transition-interactive)',
                  }}
                  onFocus={e => {
                    e.target.style.borderColor = 'var(--color-primary)';
                    e.target.style.boxShadow = '0 0 0 3px oklch(0.45 0.07 195 / 0.15)';
                  }}
                  onBlur={e => {
                    e.target.style.borderColor = error ? 'var(--color-error)' : 'var(--color-glass-border)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(v => !v)}
                  aria-label={showPwd ? 'Nascondi password' : 'Mostra password'}
                  style={{
                    position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--color-text-faint)', padding: '0.25rem',
                    display: 'flex', alignItems: 'center',
                  }}
                >
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div style={{ textAlign: 'right', marginTop: '-0.5rem' }}>
              <Link to="/forgot-password" style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--color-text-muted)',
                textDecoration: 'none',
              }}>
                Password dimenticata?
              </Link>
            </div>

            {error && (
              <div style={{
                background: 'var(--color-error-highlight)',
                border: '1px solid var(--color-error)',
                borderRadius: 'var(--radius-card)',
                padding: '0.625rem 0.875rem',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-error)',
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                position: 'relative',
                background: 'var(--color-primary)',
                border: 'none',
                borderRadius: 'var(--radius-card)',
                color: '#fff',
                fontSize: 'var(--text-base)',
                fontWeight: 600,
                padding: '0.75rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                transition: 'all var(--transition-interactive)',
                marginTop: '0.25rem',
                overflow: 'hidden',
              }}
              onMouseEnter={e => { if (!loading) (e.currentTarget.style.background = 'var(--color-primary-hover)'); }}
              onMouseLeave={e => { (e.currentTarget.style.background = 'var(--color-primary)'); }}
            >
              {loading ? 'Accesso in corso…' : 'Accedi'}
              {/* Shine effect on hover */}
              <span style={{
                position: 'absolute',
                top: 0, left: '-100%',
                width: '100%', height: '100%',
                background: 'linear-gradient(90deg, transparent, oklch(1 0 0 / 0.15), transparent)',
                pointerEvents: 'none',
              }}
                onMouseEnter={e => { e.currentTarget.style.animation = 'shine 0.6s ease-in-out'; }}
                onMouseLeave={e => { e.currentTarget.style.animation = 'none'; }}
              />
            </button>
          </form>

          {googleEnabled === true && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '1rem 0 0.75rem' }}>
                <div style={{ flex: 1, height: 1, background: 'var(--color-glass-border)' }} />
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', whiteSpace: 'nowrap' }}>oppure</span>
                <div style={{ flex: 1, height: 1, background: 'var(--color-glass-border)' }} />
              </div>

              <button
                type="button"
                onClick={() => { window.location.href = '/api/auth/google'; }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.625rem',
                  width: '100%',
                  padding: '0.625rem 0.875rem',
                  background: 'var(--color-glass-input-bg)',
                  border: '1px solid var(--color-glass-border)',
                  borderRadius: 'var(--radius-card)',
                  color: 'var(--color-text)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'background var(--transition-interactive)',
                }}
                onMouseEnter={e => { (e.currentTarget.style.background = 'var(--color-surface-3, rgba(255,255,255,0.10))'); }}
                onMouseLeave={e => { (e.currentTarget.style.background = 'var(--color-glass-input-bg)'); }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.185l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.962L3.964 6.294C4.672 4.169 6.656 3.58 9 3.58z"/>
                </svg>
                Accedi con Google
              </button>
            </>
          )}
        </div>

        {/* Link policy */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.75rem',
        }}>
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            fontSize: 'var(--text-xs)',
          }}>
            <button
              type="button"
              onClick={() => { setDisclaimerSlug('privacy_policy'); setDisclaimerOpen(true); }}
              style={{
                background: 'none', border: 'none',
                color: 'var(--color-primary)', cursor: 'pointer',
                textDecoration: 'underline', padding: 0, font: 'inherit',
              }}
            >
              Privacy Policy
            </button>
            <span style={{ color: 'var(--color-text-faint)' }}>•</span>
            <button
              type="button"
              onClick={() => { setDisclaimerSlug('cookie_policy'); setDisclaimerOpen(true); }}
              style={{
                background: 'none', border: 'none',
                color: 'var(--color-primary)', cursor: 'pointer',
                textDecoration: 'underline', padding: 0, font: 'inherit',
              }}
            >
              Cookie Policy
            </button>
            <span style={{ color: 'var(--color-text-faint)' }}>•</span>
            <button
              type="button"
              onClick={() => { setDisclaimerSlug('note_legali'); setDisclaimerOpen(true); }}
              style={{
                background: 'none', border: 'none',
                color: 'var(--color-primary)', cursor: 'pointer',
                textDecoration: 'underline', padding: 0, font: 'inherit',
              }}
            >
              Note Legali
            </button>
          </div>
        </div>

        <DisclaimerModal
          open={disclaimerOpen}
          onClose={() => setDisclaimerOpen(false)}
          slug={disclaimerSlug}
          title={disclaimerSlug === 'privacy_policy' ? 'Privacy Policy' : disclaimerSlug === 'cookie_policy' ? 'Cookie Policy' : 'Note Legali'}
        />
      </div>
    </div>
  );
}
